import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { getGallery } from '../services/dataService';
import SEO from '../components/SEO';
import HeroSection from '../components/HeroSection';
import GalleryLightbox from '../components/GalleryLightbox';
import MasonryGallery from '../components/MasonryGallery';

const GalleryPage = () => {
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const loadGallery = async () => {
      try {
        const data = await getGallery();
        setGallery(data);
      } catch (error) {
        console.error('Failed to load gallery:', error);
        setGallery([]);
      } finally {
        setLoading(false);
      }
    };

    loadGallery();
  }, []);

  const openLightbox = (index) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % gallery.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + gallery.length) % gallery.length);
  };

  if (loading) {
    return (
      <div className="bg-slate-50 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading gallery...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-slate-50 via-white to-emerald-50/30 min-h-screen">
      <SEO
        pageType="gallery"
        title="Treatment Gallery | Healing Sanctuary Images | JIVA NADI Therapy Center"
        description="Explore our treatment facility and healing sanctuary through stunning visuals. See the advanced equipment and serene environment where thousands find natural pain relief."
        keywords="treatment gallery, healing sanctuary, therapy center images, chiropractic facility, acupuncture clinic photos, wellness center"
      />

      {/* Reusable Hero Section */}
      <HeroSection
        title="Our Visual Journey"
        subtitle="Gallery of Healing"
        description="Step inside Mumbai's most advanced non-surgical healing sanctuary and witness the transformation stories of thousands of patients who have found relief through our holistic therapies."
        image="https://images.unsplash.com/photo-1522878308970-972ec5eedc0d?w=800&h=600&fit=crop"
        badge="Visual Journey"
        primaryButton={{
          text: "Explore Gallery",
          href: "#gallery-grid"
        }}
        secondaryButton={{
          text: "Book Consultation",
          href: "/contact"
        }}
        trustIndicators={[
          { value: gallery.length.toString(), label: "Gallery Images" },
          { value: "15+", label: "Years Experience" },
          { value: "30K+", label: "Transformed Lives" }
        ]}
      />

      <section id="gallery-grid" className="py-16 lg:py-24 max-w-7xl mx-auto px-4">
        {/* Gallery Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl lg:text-4xl font-black text-gray-900 mb-4">
            Explore Our Gallery
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Discover the transformative power of our holistic therapies through these inspiring images and facility photos.
          </p>
        </motion.div>

        {/* Modern Masonry Gallery */}
        <MasonryGallery 
          images={gallery}
          onImageClick={openLightbox}
        />

        {/* Gallery Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-20 text-center"
        >
          <div className="inline-flex items-center gap-8 bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-lg border border-gray-100">
            <div className="text-center">
              <div className="text-3xl font-black text-emerald-600">{gallery.length}</div>
              <div className="text-sm text-gray-600 font-medium">Gallery Images</div>
            </div>
            <div className="w-px h-12 bg-gray-200"></div>
            <div className="text-center">
              <div className="text-3xl font-black text-emerald-600">15+</div>
              <div className="text-sm text-gray-600 font-medium">Years Experience</div>
            </div>
            <div className="w-px h-12 bg-gray-200"></div>
            <div className="text-center">
              <div className="text-3xl font-black text-emerald-600">30K+</div>
              <div className="text-sm text-gray-600 font-medium">Happy Patients</div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Gallery Lightbox */}
      <GalleryLightbox
        images={gallery}
        currentIndex={currentImageIndex}
        isOpen={lightboxOpen}
        onClose={closeLightbox}
        onNext={nextImage}
        onPrev={prevImage}
      />
    </div>
  );
};

export default GalleryPage;